import { zodResolver } from '@hookform/resolvers/zod'
import { CalendarCheckIcon, CheckIcon, PlusIcon } from '@phosphor-icons/react'
import { useEffect, useMemo } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  BookingType,
  useCreateBookingType,
  useGetBookingType,
  useUpdateBookingType,
} from '@/api/booking-types'
import { AvatarFormField, FocusTarget } from '@/components/form'
import { TextAreaFormField } from '@/components/form/text-area-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Grid } from '@/components/ui/grid'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { toastFactory } from '@/factories'
import { getAssetLink } from '@/helpers/get-asset-link'
import { useDirtyFormGuard, useFormSave } from '@/hooks'
import { getPlaceName } from '@/pages/organisation/places/places.utils'
import { FormChangeFields, getFormChanges } from '@/pages/organisation/shared/form-changes'
import { PersonFormLayout } from '@/pages/organisation/shared/person-form-layout'
import { selectBuildings } from '@/reducers/buildingSelectorReducer'
import { getApiErrorMessage } from '@/utils/error'

import { UpdatedAt } from '../../shared/updated-at'
import { BuildingsSelect } from '../buildings-select'
import {
  BOOKING_TYPE_FORM_WIDTH,
  BOOKING_TYPE_IMAGE_PATH,
  BOOKING_TYPE_TEXT_MAX_LENGTH,
  BookingTypeFormData,
  getBookingTypeFormDefaults,
  makeBookingTypeFormSchema,
} from './booking-type-form-modal.constants'
import { BookingTypeColorField, BookingTypeIconField } from './booking-type-visual-fields'
import { BOOKING_NAME_MAX_LENGTH } from './group-form-modal.constants'

interface BookingTypeFormModalProps {
  bookingType?: BookingType
  category: number
  duplicate?: boolean
  nextOrder: number
  onClose: VoidFunction
  onSaved?: (id: number) => void
}

export const BookingTypeFormModal = ({
  bookingType,
  category,
  duplicate = false,
  nextOrder,
  onClose,
  onSaved,
}: BookingTypeFormModalProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { mutateAsync: createBookingType } = useCreateBookingType()
  const { mutateAsync: updateBookingType } = useUpdateBookingType()
  const { data: detail, isLoading: isLoadingDetail } = useGetBookingType(bookingType?.id)

  const isEdit = !!bookingType && !duplicate

  const schema = useMemo(
    () => makeBookingTypeFormSchema(t, { hasExistingImage: !!bookingType?.image }),
    [t, bookingType?.image],
  )

  const form = useForm<BookingTypeFormData>({
    defaultValues: getBookingTypeFormDefaults(bookingType, { duplicate }),
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (detail)
      form.reset(getBookingTypeFormDefaults(detail, { duplicate }), { keepDirtyValues: true })
  }, [detail, duplicate, form])

  const { isDirty, isSubmitting } = form.formState
  const buildings = useSelector(selectBuildings)

  const changeFields = useMemo<FormChangeFields<BookingTypeFormData>>(
    () => ({
      buildingIds: {
        items: (value) =>
          (value as number[]).map((id) => {
            const building = buildings.find((item) => item.id === id)
            return building ? getPlaceName(building) : String(id)
          }),
        label: t('organisation:bookingTypes.type.fields.buildings.label'),
      },
      color: { label: t('organisation:bookingTypes.type.fields.color.label') },
      description: { label: t('organisation:bookingTypes.type.fields.description.label') },
      icon: { label: t('organisation:bookingTypes.type.fields.icon.label') },
      image: { image: true, label: t('organisation:bookingTypes.type.fields.image.label') },
      internalName: { label: t('organisation:bookingTypes.type.fields.internalName.label') },
      link: { label: t('organisation:bookingTypes.type.fields.link.label') },
      name: { label: t('organisation:bookingTypes.type.fields.name.label') },
    }),
    [buildings, t],
  )
  const color = form.watch('color')
  const guard = useDirtyFormGuard(
    isDirty && !isSubmitting,
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    { inPopup: true },
  )

  const handleClose = () => {
    guard(onClose)
    return false
  }

  const onSubmit: SubmitHandler<BookingTypeFormData> = async (data) => {
    const fields = {
      buildingIds: data.buildingIds,
      color: data.color,
      description: data.description.trim(),
      icon: data.icon,
      image: data.image?.[0],
      internalName: data.internalName.trim(),
      link: data.link.trim(),
      name: data.name.trim(),
    }

    try {
      if (isEdit && bookingType) {
        await updateBookingType({ ...fields, id: bookingType.id })
        toastFactory.success(t('organisation:bookingTypes.type.toasts.updated'))
      } else {
        const created = await createBookingType({
          ...fields,
          category,
          duplicateId: duplicate ? bookingType?.id : undefined,
          order: nextOrder,
        })
        toastFactory.success(
          t(
            duplicate
              ? 'organisation:bookingTypes.type.toasts.duplicated'
              : 'organisation:bookingTypes.type.toasts.created',
          ),
        )
        onSaved?.(created.id)
      }
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const title = duplicate
    ? t('organisation:bookingTypes.type.modal.titleDuplicate')
    : isEdit
      ? t('organisation:bookingTypes.type.modal.titleEdit')
      : t('organisation:bookingTypes.type.modal.titleNew')

  const submit = useFormSave({ form, isEdit, onSubmit, onUnchanged: onClose })

  return (
    <Modal
      autoWidth
      title={title}
      headerIcon={CalendarCheckIcon}
      footer={
        <>
          <UpdatedAt value={isEdit ? (detail?.updatedAt ?? bookingType?.updatedAt) : undefined} />
          <Button use="secondary" onClick={handleClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button loading={isSubmitting || isLoadingDetail} use="primary" onClick={submit}>
            {isEdit ? t('common:actions.save') : t('common:actions.add')}
            <Icon icon={isEdit ? CheckIcon : PlusIcon} />
          </Button>
        </>
      }
      onBeforeClose={handleClose}
      onClose={onClose}
    >
      <PersonFormLayout form={form} width={BOOKING_TYPE_FORM_WIDTH} onSubmit={submit}>
        <AvatarFormField
          existingImageUrl={
            bookingType?.image
              ? getAssetLink({ file: bookingType.image, path: BOOKING_TYPE_IMAGE_PATH })
              : undefined
          }
          label={t('organisation:bookingTypes.type.fields.image.label')}
          name="image"
          uploadLabel={t('organisation:bookingTypes.type.fields.image.upload')}
        />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <TextInputFormField
            withReservedFooter
            required
            label={t('organisation:bookingTypes.type.fields.name.label')}
            maxLength={BOOKING_NAME_MAX_LENGTH}
            name="name"
            placeholder={t('organisation:bookingTypes.type.fields.name.placeholder')}
          />
          <TextInputFormField
            withReservedFooter
            required
            label={t('organisation:bookingTypes.type.fields.internalName.label')}
            maxLength={BOOKING_NAME_MAX_LENGTH}
            name="internalName"
            placeholder={t('organisation:bookingTypes.type.fields.internalName.placeholder')}
          />
        </Grid>

        <BookingTypeIconField color={color} />
        <BookingTypeColorField />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <Controller
            control={form.control}
            name="buildingIds"
            render={({ field, fieldState }) => (
              <FocusTarget fieldRef={field.ref}>
                <BuildingsSelect
                  withReservedFooter
                  required
                  error={fieldState.error?.message}
                  label={t('organisation:bookingTypes.type.fields.buildings.label')}
                  placeholder={t('organisation:bookingTypes.type.fields.buildings.placeholder')}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value)
                    field.onBlur()
                  }}
                  onReset={() => field.onChange([])}
                />
              </FocusTarget>
            )}
          />

          <TextInputFormField
            withReservedFooter
            label={t('organisation:bookingTypes.type.fields.link.label')}
            maxLength={BOOKING_TYPE_TEXT_MAX_LENGTH}
            name="link"
            placeholder={t('organisation:bookingTypes.type.fields.link.placeholder')}
          />
        </Grid>

        <TextAreaFormField
          withReservedFooter
          label={t('organisation:bookingTypes.type.fields.description.label')}
          maxLength={BOOKING_TYPE_TEXT_MAX_LENGTH}
          name="description"
          placeholder={t('organisation:bookingTypes.type.fields.description.placeholder')}
        />
      </PersonFormLayout>
    </Modal>
  )
}
